import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "../../Constants/AppRoutes";
import { ISensor } from "../../Models/ISensor";
import { ISentinelRich } from "../../Models/ISentinel";
import { dayAndTime, roomLabel, nameLabel } from "../../Utils/DeviceTexts";
import IconButtonItem from "../IconButtonItem/IconButtonItem";
import DeviceCard, { DEVICE_SILENT_TOOLTIP, DeviceCardOfflineMark, useDeviceCardState } from "../DeviceCard/DeviceCard";
import DeviceDetails from "../DeviceDetails/DeviceDetails";
import VideoDialog, { useVideoDialog } from "../VideoDialog/VideoDialog";
import WalkieTalkieControls from "../WalkieTalkieControls/WalkieTalkieControls";
import "./MonitoringCardGateway.scss";

export interface IMonitoringCardGatewayProps {
  sentinel: ISentinelRich;
  index: number;
}

// livello della batteria: 0-3, 4 = alimentata dal cavo
const BATTERY_IMAGE = ["battery_dead.png", "battery_low.png", "battery_mid.png", "battery_full.png", "power_plug.svg"];
const BATTERY_TEXT = ["Batteria scarica", "Batteria quasi scarica", "Batteria a metà", "Batteria carica", "Con il cavo"];

const MonitoringCardGateway = (props: IMonitoringCardGatewayProps) => {
  const navigate = useNavigate();
  const [details, setDetails] = useState<HTMLElement | null>(null);
  const state = useDeviceCardState(props.sentinel);

  const doorSensor = useMemo(
    () => props.sentinel.sensors.find((x: ISensor) => x.type === "door"),
    [props.sentinel.sensors]
  );
  const room = roomLabel(props.sentinel.patient.room);
  const video = useVideoDialog();
  const hasCamera = !!props.sentinel.cameraUrl && props.sentinel.cameraUrl.trim() !== "";
  const name = nameLabel(props.sentinel.patient.identifier);

  const configure = (
    <IconButtonItem
      iconName="Settings"
      tooltip="Configura Varco"
      onClick={() => {
        navigate(
          AppRoutes.APP_CONFIGURE_PAGE +
            "?sentinelId=" +
            props.sentinel.id
        );
      }}
    />
  );

  // Ascolta e Parla come sul letto: solo se la porta ha il modulo vocale (Setup, matita di Audio) ed e' accesa
  const hasAudio = !!props.sentinel.audioEnabled && !!props.sentinel.enabled;
  const videoButton =
    hasAudio || hasCamera ? (
      <>
        {hasAudio && (
          <WalkieTalkieControls
            sentinelId={props.sentinel.id}
            compactMode={false}
            activatedBy="manual"
            unavailableLabel={state.silent ? DEVICE_SILENT_TOOLTIP : undefined}
          />
        )}
        {hasCamera && <IconButtonItem iconName="Video" tooltip="Visualizza live" onClick={video.show} />}
        <span className="device-card-sep" />
      </>
    ) : null;

  if (!doorSensor) {
    return (
      <DeviceCard sentinel={props.sentinel} state={state} kind="gateway">
        <div className="gateway-card-body">
          <div className="gateway-card-title">
            <div className="device-card-name">{name}</div>
            <div className="gateway-card-room">Sensore porta non disponibile</div>
          </div>
          <div className="device-card-buttons">
            {videoButton}
            {configure}
          </div>
        </div>
        {video.open && <VideoDialog sentinel={props.sentinel} name={name} onDismiss={video.close} />}
      </DeviceCard>
    );
  }

  // Il subscriber MQTT manda in lastValue SOLO lo stato della porta (0/1: cosi' il backend vede la
  // transizione chiusa->aperta) e mette il livello batteria (0..3) in threshold tramite la rotta
  // keepalive. Leggere la batteria da lastValue/10 dava sempre "scarica" (13/09).
  const batteryLevel = doorSensor.threshold == null ? 3 : Math.min(4, Math.max(0, +doorSensor.threshold));
  const doorOpen = +(doorSensor.lastValue ?? 0) % 10 === 1; // tollera vecchi valori combinati

  return (
    <DeviceCard sentinel={props.sentinel} state={state} kind="gateway">
      <div className="gateway-card-body">
        <button
          type="button"
          className="device-card-icon-button gateway-card-icon"
          title="Dettagli"
          aria-label={"Dettagli di " + name}
          onClick={(e) => setDetails(e.currentTarget)}
        >
          <img src="./assets/exit-room.png" alt="Porta" />
          <DeviceCardOfflineMark state={state} />
        </button>
        <div className="gateway-card-title">
          <div className="device-card-name">{name}</div>
          {room !== "" && <div className="gateway-card-room">Stanza: {room}</div>}
        </div>
        <div className="device-card-buttons">
          {videoButton}
          {configure}
        </div>
      </div>

      <div className="gateway-card-row">
        <span className={doorOpen ? "door-open" : "door-closed"}>{doorOpen ? "Aperta" : "Chiusa"}</span>
        <span>
          <img src={"./assets/" + BATTERY_IMAGE[batteryLevel]} alt="" />
          {BATTERY_TEXT[batteryLevel]}
        </span>
        <span className="gateway-card-dim">Ultimo dato {dayAndTime(doorSensor.updatedAt)}</span>
        <span className="gateway-card-dim">ID Varco: {props.sentinel.id}</span>
      </div>

      {details && (
        <DeviceDetails
          sentinel={props.sentinel}
          target={details}
          alarmTypes={state.alarmTypes}
          testTypes={state.testTypes}
          onDismiss={() => setDetails(null)}
        />
      )}

      {video.open && <VideoDialog sentinel={props.sentinel} name={name} onDismiss={video.close} />}
    </DeviceCard>
  );
};

// Esporta il componente senza memoizzazione personalizzata
// per permettere il re-render quando il tempo passa e lo stato offline cambia
export default MonitoringCardGateway;
